import { api } from './api';

let validation: { refreshToken: string; promise: Promise<unknown> } | null = null;

async function validateSession(refreshToken: string): Promise<unknown> {
  if (validation?.refreshToken === refreshToken) return validation.promise;
  // Reuse the API client's refresh and session-expired handling. Coalesce
  // parallel fetch failures in this tab into a single session validation.
  const promise = api.get('/auth/me');
  validation = { refreshToken, promise };
  try {
    return await promise;
  } finally {
    if (validation?.promise === promise) validation = null;
  }
}

/** Recover the same session after a 401, sharing validation across fetches and uploads. */
export async function refreshSessionForRetry(
  accessToken: string | null,
  refreshToken: string | null,
  signal?: AbortSignal | null,
  sessionId: string | null = sessionStorage.getItem('auth_session_id'),
): Promise<string | null> {
  if (!refreshToken || signal?.aborted) return null;
  // Never replay an earlier account's operation after logout or a new login.
  const sameSession = () => sessionId
    ? sessionStorage.getItem('auth_session_id') === sessionId
    : sessionStorage.getItem('refresh_token') === refreshToken;
  if (!sameSession()) return null;
  if (sessionStorage.getItem('access_token') === accessToken) {
    await validateSession(refreshToken);
  }
  const nextToken = sessionStorage.getItem('access_token');
  if (!nextToken || !sameSession() || signal?.aborted) {
    return null;
  }
  return nextToken;
}

/** Fetch a protected API URL; retry a rejected credential once, before any SSE body is read. */
export async function fetchWithSession(url: string, init: RequestInit = {}): Promise<Response> {
  const accessToken = sessionStorage.getItem('access_token');
  const refreshToken = sessionStorage.getItem('refresh_token');
  const sessionId = sessionStorage.getItem('auth_session_id');
  const response = await fetch(url, init);
  if (response.status !== 401) return response;
  const nextToken = await refreshSessionForRetry(accessToken, refreshToken, init.signal, sessionId);
  if (!nextToken) return response;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${nextToken}`);
  return fetch(url, { ...init, headers });
}
